import { useRef, useState } from 'react';
import { Icon } from '../../../shared/ui/Primitives';
import type { ScenarioProjection } from '../domain/types';
import { ScenarioCardArt } from './ScenarioCardArt';
import { forestIssues } from './forest-research-content';
import { chemicalIssues } from './chemical-research-content';
import { transportIssues } from './transport-research-content';
import { floodIssues } from './flood-research-content';
import { modalDismissProps, useModalFocus } from './scenario-modal-focus';
import {
  findScenarioCard,
  issueContent,
  scenarioCards,
  type ScenarioCardContent,
} from './scenario-center-content';

/**
 * The scene library — page A of the Scenario Center.
 *
 * THE FIVE CARDS ALWAYS RENDER. The user's confirmed decision is that a scenario whose
 * content is still being prepared stays visible, keeps its own Chinese state label and
 * opens a short scope note. Search and category are filters over the view; neither is a
 * content status, and neither may permanently hide a card. The old "内容状态" selector is
 * gone: it filtered on an internal formation state and told a business reader nothing.
 */
export interface LibraryFilters {
  readonly query: string;
  readonly category: string;
}

export const libraryCategories = [
  '',
  ...new Set(scenarioCards.map((card) => card.category)),
] as const;

/**
 * What the search box matches: the card's own Chinese text plus the titles of the typical
 * issues the scenario actually carries. The placeholder says exactly this, because a
 * search that claims to cover more than it does is its own kind of wrong answer.
 */
export function librarySearchText(card: ScenarioCardContent): string {
  const issues =
    card.id === 'forest-fire'
      ? forestIssues.map((issue) => issue.title)
      : card.id === 'chemical'
        ? chemicalIssues.map((issue) => issue.title)
        : card.id === 'transport'
          ? transportIssues.map((issue) => issue.title)
          : card.id === 'flood'
            ? floodIssues.map((issue) => issue.title)
            : card.id === 'high-rise-building-fire'
              ? issueContent.map((issue) => issue.title + ' ' + issue.key)
              : [];
  return [
    card.name,
    card.id === 'forest-fire' ? 'forest' : '',
    card.category,
    card.summary,
    card.scope,
    ...card.topics,
    ...issues,
  ]
    .join(' ')
    .toLocaleLowerCase();
}

export function filterLibrary(filters: LibraryFilters): readonly ScenarioCardContent[] {
  const needle = filters.query.trim().toLocaleLowerCase();
  return scenarioCards.filter(
    (card) =>
      (!filters.category || card.category === filters.category) &&
      (!needle || librarySearchText(card).includes(needle)),
  );
}

export function ScenarioLibraryView({
  data,
  filters,
  initialScope,
  onFilters,
  onEnter,
}: {
  readonly data: ScenarioProjection;
  readonly filters: LibraryFilters;
  /** A scenario a deep link asked to open the scope note for. */
  readonly initialScope: string | null;
  readonly onFilters: (filters: LibraryFilters) => void;
  readonly onEnter: (scenarioId: string) => void;
}) {
  const [planning, setPlanning] = useState<string | null>(initialScope);
  const search = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const rows = filterLibrary(filters);
  const plannedCard = planning ? findScenarioCard(planning) : undefined;
  const planned = plannedCard?.state === 'IN_PREPARATION' ? plannedCard : undefined;
  const issueCount = issueContent.length;
  const floodIssueCount = floodIssues.length;
  const formedCount = data.entries.risks.length;
  return (
    <section className="scc-library" aria-label="场景库">
      <div className="scc-library-bar scc-panel">
        <div className="scc-library-search">
          <label htmlFor="scc-library-search">搜索场景</label>
          <input
            ref={search}
            id="scc-library-search"
            type="search"
            placeholder="搜索场景名称、类别或典型问题，例如：高层、烟气、洪水…"
            value={filters.query}
            onChange={(event) => onFilters({ ...filters, query: event.target.value })}
          />
        </div>
        <div className="scc-category-bar" aria-label="灾害类别">
          {libraryCategories.map((category) => (
            <button
              key={category || 'all'}
              aria-pressed={filters.category === category}
              onClick={() => onFilters({ ...filters, category })}
            >
              {category || '全部场景'}
            </button>
          ))}
        </div>
      </div>
      <div className="scc-library-grid" data-library-results>
        {rows.map((card) => (
          <button
            type="button"
            className="scc-card"
            key={card.id}
            data-scenario={card.id}
            data-card-state={card.state}
            onClick={(event) => {
              opener.current = event.currentTarget;
              if (card.state === 'REFERENCE') onEnter(card.id);
              else setPlanning(card.id);
            }}
          >
            <span className="scc-card-art">
              <ScenarioCardArt id={card.id} name={card.name} />
              <span className="scc-card-state">{card.stateLabel}</span>
            </span>
            <span className="scc-card-body">
              <strong className="scc-card-title">{card.name}</strong>
              <span className="scc-card-summary">{card.summary}</span>
              <span className="scc-card-topics">
                {card.topics.map((topic) => (
                  <span key={topic}>{topic}</span>
                ))}
              </span>
              <span className="scc-card-action">
                {card.action}
                <span aria-hidden="true"> →</span>
              </span>
            </span>
          </button>
        ))}
      </div>
      {rows.length === 0 && (
        <div className="scc-panel scc-empty" role="status" data-read-status="EMPTY_FILTER_RESULT">
          <h2>没有匹配的场景</h2>
          <p>换一个关键词，或清空筛选。五个场景始终都在场景库中。</p>
          <button
            onClick={() => {
              onFilters({ query: '', category: '' });
              search.current?.focus();
            }}
          >
            清空筛选
          </button>
        </div>
      )}
      <p className="scc-library-caption">
        场景库共 {scenarioCards.length} 个场景，均已提供公开资料研究参考；高层建筑火灾现有{' '}
        {issueCount} 个典型问题、{formedCount}{' '}
        条源风险记录；森林火灾、危化品事故、交通与地下空间事故 各提供 6
        个问题的研究参考，洪水与城市内涝提供 {floodIssueCount} 个问题的研究参考。
      </p>
      {planned && (
        <ScenarioScopeDialog
          card={planned}
          restoreFocusTo={opener.current}
          onClose={() => setPlanning(null)}
        />
      )}
    </section>
  );
}

/**
 * The scope note a scenario-in-preparation opens.
 *
 * It carries a name, one scope sentence, the directions being prepared, and a way back.
 * It deliberately has no subscribe button, no schedule, no progress bar, no fabricated
 * report and no "use template" action.
 */
function ScenarioScopeDialog({
  card,
  restoreFocusTo,
  onClose,
}: {
  readonly card: ScenarioCardContent;
  readonly restoreFocusTo?: HTMLElement | null;
  readonly onClose: () => void;
}) {
  const dialog = useModalFocus(restoreFocusTo);
  return (
    <dialog
      className="scc-scope-dialog"
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scc-scope-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-scope-art">
        <ScenarioCardArt id={card.id} name={card.name} />
      </div>
      <button className="scc-scope-close" onClick={onClose} aria-label="关闭">
        ×
      </button>
      <span className="scc-chip">{card.stateLabel}</span>
      <h2 id="scc-scope-title">{card.name}</h2>
      <p className="scc-scope-summary">{card.summary}</p>
      <ul className="scc-scope-directions">
        {card.directions.map((direction) => (
          <li key={direction}>
            <Icon name="info" />
            {direction}
          </li>
        ))}
      </ul>
      <p className="scc-scope-note">
        该场景内容正在建设中，后续将逐步补充典型问题、需要掌握的信息、所需能力及相关依据。
      </p>
      <button className="scc-primary scc-wide" autoFocus onClick={onClose}>
        返回场景库
      </button>
    </dialog>
  );
}

/** The route a card enters. Only the formed scenario has one today. */
export function scenarioEntryPath(card: ScenarioCardContent): string | null {
  return card.state === 'REFERENCE' ? card.id : null;
}
